import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';
import { gridLocale } from '@h20-shared/grids/grid-context';
import { FALLBACK_LOCALE } from '@h20-shared/utils/number-format-utils';
import { formatMultiValue } from './multi-value.formatter';
import type { MultiValueCellOptions, MultiValueCellParams } from './multi-value.types';

@Component({
    selector: 'app-multi-value-cell',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent, NgbTooltip, NgComponentOutlet, TranslateModule],
    templateUrl: './multi-value-cell.component.html',
})
export class MultiValueCellComponent implements ICellRendererAngularComp {
    protected readonly options = signal<MultiValueCellOptions>({ getNumbers: () => null });
    private readonly locale = signal(FALLBACK_LOCALE);

    protected readonly text = computed<string | null>(() => {
        const numbers = this.options().getNumbers();
        if (numbers === null || numbers.length === 0) {
            return null;
        }
        return formatMultiValue(numbers, this.locale(), this.options().separator);
    });

    protected readonly tooltip = computed(() => {
        const tooltip = this.options().tooltip;
        if (tooltip === undefined) {
            return null;
        }
        const inputs = tooltip.getInputs();
        if (inputs === null) {
            return null;
        }
        return { component: tooltip.component, inputs, tooltipClass: tooltip.tooltipClass ?? '' };
    });

    agInit(params: ICellRendererParams & MultiValueCellParams): void {
        this.options.set(params.cellOptions);
        this.locale.set(gridLocale(params.context));
    }

    refresh(params: ICellRendererParams & MultiValueCellParams): boolean {
        this.agInit(params);
        return true;
    }
}
